'use client';

import { useState, useMemo } from 'react';
import { AtArrow } from '@/components/atoms/at-arrow/at-arrow';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtText } from '@/components/atoms/at-text/at-text';
import { EThemes } from '@/lib/contentstack-sdk/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';
import 'swiper/css';
import { MlLightbox } from '@/components/molecules';
import { mapBlueprintsToSlides } from '@/components/molecules/ml-lightbox/utils';

interface IBlueprintsCarouselProps {
  blueprints: {
    title: string;
    subtitle?: string;
    image: any;
  }[];
  theme: EThemes;
}

const FloorPlanViewerClient = ({
  blueprints,
  theme,
}: IBlueprintsCarouselProps) => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const [openViewer, setOpenViewer] = useState<boolean>(false);

  const handlePrevClick = () => {
    if (!swiper) return;

    const newIndex =
      swiperIndex === 0 ? blueprints.length - 1 : swiperIndex - 1;
    setSwiperIndex(newIndex);
    swiper.slideTo(newIndex);
  };

  const handleNextClick = () => {
    if (!swiper) return;

    const newIndex =
      swiperIndex === blueprints.length - 1 ? 0 : swiperIndex + 1;
    setSwiperIndex(newIndex);
    swiper.slideTo(newIndex);
  };

  const handleOpenClick = () => {
    setOpenViewer(true);
    const chatEl = document.getElementById('genesys-messenger');
    const iframe = chatEl?.querySelector(
      'div > iframe',
    ) as HTMLIFrameElement | null;
    if (iframe) iframe.style.zIndex = '999';
  };

  const arrowsContainerTheme = useMemo(() => {
    return theme === EThemes.DARK ? EThemes.LIGHT : EThemes.DARK;
  }, [theme]);

  const currentBlueprint = blueprints[swiperIndex];
  const viewerSlides = mapBlueprintsToSlides(blueprints);
  return (
    <>
      <div className="box-border h-auto w-full md:px-7">
        <Swiper
          slidesPerView={1}
          className="h-full w-full"
          onSwiper={setSwiper}
          a11y={{
            prevSlideMessage: 'Previous Blueprint',
            nextSlideMessage: 'Next Blueprint',
          }}
        >
          {blueprints.map((blueprint, i) => (
            <SwiperSlide key={`${i}_${blueprint.title}`}>
              <div className="relative mx-auto h-230 w-full md:h-500">
                <button
                  className="absolute h-full w-full"
                  onClick={() => handleOpenClick()}
                >
                  <AtMedia
                    {...blueprint.image}
                    className="!object-contain"
                    sizes="(max-width: 1023px) 100vw, 33.33vw"
                  />
                </button>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div
        data-theme={arrowsContainerTheme}
        className="mb-12 mt-3 flex w-full flex-col items-center justify-between gap-6 rounded-30 bg-skin-secondary px-9 py-4 md:mb-0 md:flex-row md:gap-2"
      >
        <div className="text-center md:text-left">
          <AtText type="p" size="sm">
            {swiperIndex + 1}/{blueprints.length}
          </AtText>
          <AtHeader element="p" variant="h4">
            {currentBlueprint.title}
          </AtHeader>
          {currentBlueprint.subtitle && (
            <AtText type="p" size="sm" className="text-skin-tertiary">
              {currentBlueprint.subtitle}
            </AtText>
          )}
        </div>
        <div className="flex h-full gap-4">
          <AtArrow direction="prev" theme={theme} onClick={handlePrevClick} />
          <AtArrow direction="next" theme={theme} onClick={handleNextClick} />
        </div>
      </div>
      <MlLightbox
        slides={viewerSlides}
        isOpen={openViewer}
        index={swiperIndex}
        handleClose={() => setOpenViewer(false)}
      />
    </>
  );
};

export default FloorPlanViewerClient;
